import React, { useRef, useState } from "react";
import pickBy from "lodash/pickBy";
import isEmpty from "lodash/isEmpty";
import { globalModalsZIndex } from "@smartsuite/react-ui/lib/constants/zIndex";
import { DropdownPanel } from "@smartsuite/react-ui/lib/components/dropdowns/DropdownPanel/DropdownPanel";
import { MenuLineSeparator } from "@smartsuite/react-ui/lib/components/dropdowns/MenuLineSeparator/MenuLineSeparator";
import { Text } from "@smartsuite/react-ui/lib/components/typography/Text/Text";
import { EditorToolbarDropdownButton } from "../../../../react/EditorToolbarDropdownButton/EditorToolbarDropdownButton";
import { text } from "../../text";
import { Hotkeys } from "../../../slash/components/Hotkeys/Hotkeys";
import { Icon } from "@smartsuite/react-ui/lib/components/utils/Icon";
const iconDimension = {
    width: 14,
    height: 14,
};
export const TextStyleDropdownButton = ({ state, commands, disableTooltips = false, items = getDefaultItems(commands), showOnlyIcon = true, onCommand, }) => {
    var _a, _b;
    const [isOpened, setIsOpened] = useState(false);
    const buttonRef = useRef();
    const activeItem = (_b = (_a = Object.entries(items).find(([, item]) => item.command.active(state))) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : items.paragraph;
    const handleClick = (command) => () => {
        onCommand(command);
        setIsOpened(false);
    };
    if (isEmpty(items) || Object.keys(items).length === 1) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(EditorToolbarDropdownButton, { ref: buttonRef, title: text.textStyle, isOpened: isOpened, disableTooltip: disableTooltips, onClick: () => setIsOpened((opened) => !opened) }, showOnlyIcon ? (React.createElement(Icon, { name: "formatting-heading", size: "S", color: "currentColor", dimension: iconDimension })) : (React.createElement(Text, { size: "s", wrapMode: "nowrap" }, activeItem.title))),
        React.createElement(DropdownPanel, { isActive: isOpened, targetElementRef: buttonRef, zIndex: globalModalsZIndex, minWidth: 232, onClickOutside: () => setIsOpened(false) },
            React.createElement("div", { className: "text-style-dropdown-list" }, Object.entries(items).map(([key, item]) => {
                const { height, fontSize, fontWeight, color } = getTextDropdownStyles(item.type);
                return (React.createElement(React.Fragment, { key: key },
                    React.createElement("button", { className: `text-style-dropdown-list__item text-style-dropdown-list__item--${key}`, style: { height }, onClick: handleClick(item.command) },
                        React.createElement(Text, { className: "text-style-dropdown-list__text", color: color || "currentColor", styles: { fontWeight, fontSize } }, item.title),
                        false && React.createElement(Hotkeys, { signKey: item.lastHotkey })),
                    item.separator && React.createElement(MenuLineSeparator, null)));
            })))));
};
const getTextDropdownStyles = (type) => {
    var _a;
    const styles = {
        "normal-text": {
            fontSize: 14,
            height: 36,
        },
        "small-text": {
            fontSize: 13,
            height: 36,
        },
        heading1: {
            fontSize: 24,
            height: 40,
            fontWeight: 600,
        },
        heading2: {
            fontSize: 20,
            height: 36,
            fontWeight: 600,
        },
        heading3: {
            fontSize: 16,
            height: 36,
            fontWeight: 600,
        },
        heading4: {
            fontSize: 14,
            height: 36,
            fontWeight: 600,
        },
        heading5: {
            fontSize: 12,
            height: 36,
            fontWeight: 600,
        },
        heading6: {
            fontSize: 12,
            height: 36,
            fontWeight: 600,
        },
        codeSnippet: {
            fontSize: 13,
            height: 36,
        },
        "heading1-dashboards": {
            fontSize: 44,
            height: 69,
            fontWeight: 600,
            color: "primary",
        },
        "heading2-dashboards": {
            fontSize: 34,
            height: 57,
            fontWeight: 600,
            color: "primary",
        },
        "heading3-dashboards": {
            fontSize: 24,
            height: 45,
            fontWeight: 600,
            color: "primary",
        },
        "heading4-dashboards": {
            fontSize: 20,
            height: 42,
            fontWeight: 600,
            color: "primary",
        },
        "heading5-dashboards": {
            fontSize: 18,
            height: 34,
            fontWeight: 400,
            color: "primary",
        },
        "heading6-dashboards": {
            fontSize: 16,
            height: 34,
            fontWeight: 400,
            color: "primary",
        },
        "normal-text-dashboards": {
            fontSize: 14,
            height: 34,
            color: "primary",
        },
        "small-text-dashboards": {
            fontSize: 12,
            height: 34,
            color: "primary",
        },
        caption: {
            fontSize: 12,
            height: 34,
            color: "primary",
            fontWeight: 600,
        },
    };
    return (_a = styles[type]) !== null && _a !== void 0 ? _a : { fontSize: 13, height: 34 };
};
export const getDefaultItems = (commands) => {
    var _a, _b, _c, _d, _e, _f;
    const allItems = {
        paragraph: {
            title: text.normalText,
            command: commands.applyParagraph,
            separator: false,
            lastHotkey: "0",
            type: "normal-text",
        },
        paragraphSmall: {
            title: text.smallText,
            command: commands.applyParagraphSmall,
            separator: true,
            lastHotkey: "S",
            type: "small-text",
        },
        heading1: {
            title: text.heading1,
            command: (_a = commands.applyHeading) === null || _a === void 0 ? void 0 : _a.call(commands, 1),
            separator: false,
            lastHotkey: "1",
            type: "heading1",
        },
        heading2: {
            title: text.heading2,
            command: (_b = commands.applyHeading) === null || _b === void 0 ? void 0 : _b.call(commands, 2),
            separator: false,
            lastHotkey: "2",
            type: "heading2",
        },
        heading3: {
            title: text.heading3,
            command: (_c = commands.applyHeading) === null || _c === void 0 ? void 0 : _c.call(commands, 3),
            separator: false,
            lastHotkey: "3",
            type: "heading3",
        },
        heading4: {
            title: text.heading4,
            command: (_d = commands.applyHeading) === null || _d === void 0 ? void 0 : _d.call(commands, 4),
            separator: false,
            lastHotkey: "4",
            type: "heading4",
        },
        heading5: {
            title: text.heading5,
            command: (_e = commands.applyHeading) === null || _e === void 0 ? void 0 : _e.call(commands, 5),
            separator: false,
            lastHotkey: "5",
            type: "heading5",
        },
        heading6: {
            title: text.heading6,
            command: (_f = commands.applyHeading) === null || _f === void 0 ? void 0 : _f.call(commands, 6),
            separator: true,
            lastHotkey: "6",
            type: "heading6",
        },
        codeBlock: {
            title: text.codeSnippet,
            command: commands.applyCodeBlock,
            separator: false,
            lastHotkey: "7",
            type: "codeSnippet",
        },
    };
    return pickBy(allItems, (item) => !!item.command); // filter out missing commands
};
